<template>
  <div class="row">
    <div class="col-12">
      <h5 data-cy="application-form-section-tenant">
        {{ $t(isGuarantor ? 'message.applicationForm.infoAboutGuarantor' : 'message.applicationForm.infoAboutTenant') }}
      </h5>
    </div>
    <div class="col-12 col-md-12 mb-2">
      <label
        for="preferredLanguage"
        class="mb-0">
        {{ $t('message.contact.preferredLanguage') }}
      </label>
      <coozzy-form-select
        id="preferredLanguage"
        v-model="preferredLanguage"
        data-cy="application-form-individual-preferred-language-select">
        <option value="PREFERRED_LANGUAGE_GERMAN">
          {{ $t('message.contact.preferredLanguageValue.PREFERRED_LANGUAGE_GERMAN') }}
        </option>
        <option value="PREFERRED_LANGUAGE_ENGLISH">
          {{ $t('message.contact.preferredLanguageValue.PREFERRED_LANGUAGE_ENGLISH') }}
        </option>
        <option value="PREFERRED_LANGUAGE_ITALIAN">
          {{ $t('message.contact.preferredLanguageValue.PREFERRED_LANGUAGE_ITALIAN') }}
        </option>
        <option value="PREFERRED_LANGUAGE_FRENCH">
          {{ $t('message.contact.preferredLanguageValue.PREFERRED_LANGUAGE_FRENCH') }}
        </option>
      </coozzy-form-select>
    </div>
    <!-- sameAsMainIndividual -->
    <div
      v-if="mainIndividualId !== individualId && !isGuarantor"
      class="col-12 mb-4">
      <coozzy-form-checkbox
        :id="'check_' + individualId"
        v-model="sameAddressAsMainTenant"
        :initial="sameAddressAsMainTenant"
        name="sameAddressAsMainTenant"
        class="mt-4"
        data-cy="application-form-individual-same-address-checkbox">
        {{ $t('message.applicationForm.sameAsMainIndividual') }}
      </coozzy-form-checkbox>
    </div>
    <template v-if="!isMainIndividual && individualId">
      <div class="col-12">
        <coozzy-form-input
          v-model="relationToMainIndividual"
          data-cy="application-form-individual-relation-input"
          :name="$t('message.applicationForm.relationToMainIndividual') + (sending ? '*' : '')"
          type="text"
          :state="$v.relationToMainIndividual.$dirty && $v.relationToMainIndividual.$error ? false : null" />
      </div>
    </template>
    <!-- has the viewing Done -->
    <div
      v-if="isMainIndividual"
      class="col-6 col-md-6">
      <label for="viewingDone">{{ $t('message.applicationForm.hasViewingDone') }}
        {{ sending ? '*' : '' }}</label>
      <coozzy-form-select
        id="viewingDone"
        v-model="viewingDone"
        data-cy="application-form-individual-viewing-done-select">
        <option value="no">
          {{ $t('message.generic.selectField.no') }}
        </option>
        <option value="yes">
          {{ $t('message.generic.selectField.yes') }}
        </option>
      </coozzy-form-select>
    </div>
    <!-- Desired start date of the contract -->
    <div
      v-if="isMainIndividual"
      class="col-12 col-md-6">
      <coozzy-form-input
        :id="'desiredStartOfContract_' + individualId"
        ref="desiredStartOfContract"
        v-model="desiredStartOfContractDate"
        data-cy="application-form-individual-desired-start-input"
        :filter="'formatDate'"
        :name="$t('message.applicationForm.desiredStartOfContract') + (sending ? '*' : '')"
        :state="$v.dates.desiredStartOfContract.$dirty && $v.dates.desiredStartOfContract.$error ? false : null"
        type="date"
        @blur="syncDateFieldsFromInputs" />
    </div>
    <!-- Gender -->
    <div class="col-12">
      <label class="d-block">{{ $t('message.generic.title.title') }} {{ sending ? '*' : '' }}</label>
      <coozzy-form-radio
        v-model="gender"
        :default-model="gender"
        :name="'gender_' + individualId"
        class="mr-3"
        data-cy="application-form-individual-salutation-mr-radio"
        select-value="MALE"
        :state="$v.gender.$dirty && $v.gender.$error ? false : null">
        {{ $t('message.generic.title.mr') }}
      </coozzy-form-radio>
      <coozzy-form-radio
        v-model="gender"
        :default-model="gender"
        :name="'gender_' + individualId"
        class="mr-3"
        data-cy="application-form-individual-salutation-ms-radio"
        select-value="FEMALE">
        {{ $t('message.generic.title.mrs') }}
      </coozzy-form-radio>
      <coozzy-form-radio
        v-model="gender"
        :default-model="gender"
        :name="'gender_' + individualId"
        class="mr-3"
        data-cy="application-form-individual-salutation-divers-radio"
        select-value="OTHER">
        {{ $t('message.generic.title.divers') }}
      </coozzy-form-radio>
    </div>
    <!-- Name -->
    <div class="col-12 col-md-6">
      <coozzy-form-input
        :id="individualId"
        v-model="firstName"
        data-cy="application-form-individual-first-name-input"
        :name="$t('message.generic.firstName') + (sending ? '*' : '')"
        :state="$v.firstName.$dirty && $v.firstName.$error ? false : null"
        type="text" />
    </div>
    <div class="col-12 col-md-6">
      <coozzy-form-input
        :id="individualId"
        v-model="lastName"
        data-cy="application-form-individual-last-name-input"
        :name="$t('message.generic.lastName') + (sending ? '*' : '')"
        :state="$v.lastName.$dirty && $v.lastName.$error ? false : null"
        type="text" />
    </div>
    <!-- Address -->
    <div class="col-12 col-md-6">
      <coozzy-form-input
        :id="individualId"
        v-model="street"
        data-cy="application-form-individual-street-input"
        :disabled="sameAddressAsMainTenant"
        :name="$t('message.generic.street') + (sending ? '*' : '')"
        :state="$v.street.$dirty && $v.street.$error ? false : null"
        type="text" />
    </div>
    <div class="col-12 col-md-6">
      <coozzy-form-input
        :id="individualId"
        v-model="streetNumber"
        data-cy="application-form-individual-house-number-input"
        :disabled="sameAddressAsMainTenant"
        :name="$t('message.generic.streetNumber') + (sending ? '*' : '')"
        :state="$v.streetNumber.$dirty && $v.streetNumber.$error ? false : null"
        type="text" />
    </div>
    <div class="col-12 col-md-6">
      <coozzy-form-input
        :id="individualId"
        v-model="zip"
        data-cy="application-form-individual-zip-input"
        :disabled="sameAddressAsMainTenant"
        type="text"
        :prevent-letters="country === 'CH' || country === 'DE'"
        :limit="zipLength"
        :name="$t('message.generic.zip') + (sending ? '*' : '')"
        :state="$v.zip.$dirty && $v.zip.$error ? false : null"
        @input="getCityFromZip"
        @keydown.tab="isCitiesSuggested ? tabClicked : null" />
    </div>
    <div class="col-12 col-md-6">
      <coozzy-form-input
        v-if="!isCitiesSuggested"
        :id="'city-input-' + individualId"
        v-model="city"
        data-cy="application-form-individual-city-input"
        :disabled="sameAddressAsMainTenant"
        :name="$t('message.generic.city') + (sending ? '*' : '')"
        :state="$v.city.$dirty && $v.city.$error ? false : null"
        type="text" />
      <template v-else>
        <label :for="'city-suggest-' + individualId">{{ $t('message.generic.city') }}*</label>
        <coozzy-form-select
          :id="'city-suggest-' + individualId"
          v-model="citySelect"
          data-cy="application-form-individual-city-select"
          :state="$v.city.$dirty && $v.city.$error ? false : null"
          @change="cityChanged">
          <option
            v-for="(cityOption, cityIndex) in citiesValue"
            :key="cityIndex"
            :value="cityOption"
            :selected="cityIndex === 0">
            {{ cityLabels[cityIndex] || '' }}
          </option>
        </coozzy-form-select>
      </template>
    </div>
    <div class="col-12 col-md-6">
      <coozzy-form-country-select
        :id="individualId"
        v-model="country"
        data-cy="application-form-individual-country-select"
        :initial-value="country"
        :disabled="sameAddressAsMainTenant"
        :state="$v.country.$dirty && $v.country.$error ? false : null"
        :required="true" />
    </div>
    <div class="col-12 col-md-6">
      <coozzy-form-region-select
        :id="individualId"
        v-model="state"
        data-cy="application-form-individual-canton-select"
        :initial-value="state"
        :disabled="sameAddressAsMainTenant"
        :state="$v.state.$dirty && $v.state.$error ? false : null"
        :country="country" />
    </div>
    <!-- Birth -->
    <div class="col-12 col-md-6">
      <coozzy-form-input
        :id="individualId"
        ref="dob"
        v-model="dobDate"
        data-cy="application-form-individual-birthday-input"
        :filter="'formatDate'"
        :name="$t('message.generic.birthday') + (sending ? '*' : '')"
        type="date"
        :state="$v.dates.dob.$dirty && $v.dates.dob.$error ? false : null"
        @blur="syncDateFieldsFromInputs" />
    </div>
    <div
      class="col-12 col-md-6"
      data-cy="application-form-individual-nationality-select">
      <coozzy-form-country-citizenship-select
        :id="`nationality${individualId}`"
        v-model="nationality"
        :label="$tc('message.generic.nationality.title')"
        :required="sending"
        :state="$v.nationality.$dirty && $v.nationality.$error ? false : null"
        :initial-value="nationality" />
    </div>
    <!-- Residence status -->
    <div
      v-if="requiresResidenceStatus"
      class="col-12 col-md-3">
      <label :for="`residenceStatus${individualId}`">{{ $t('message.generic.residenceStatus') }}
        {{ sending ? '*' : '' }}</label>
      <coozzy-form-select
        :id="`residenceStatus${individualId}`"
        v-model="residenceStatus"
        data-cy="application-form-individual-residence-status-select"
        :state="$v.residenceStatus.$dirty && $v.residenceStatus.$error ? false : null">
        <option value="UNDEFINED_RESIDENCE_STATUS">
          -
        </option>
        <option value="RESIDENCE_STATUS_B_STAY">
          {{ $t('message.generic.residenceStatusB') }}
        </option>
        <option value="RESIDENCE_STATUS_C_SETTLE">
          {{ $t('message.generic.residenceStatusC') }}
        </option>
        <option value="RESIDENCE_STATUS_CI_STAY_EMPLOYMENT">
          {{ $t('message.generic.residenceStatusCi') }}
        </option>
        <option value="RESIDENCE_STATUS_G_BORDER_CROSSER">
          {{ $t('message.generic.residenceStatusG') }}
        </option>
        <option value="RESIDENCE_STATUS_L_SHORT_STAY">
          {{ $t('message.generic.residenceStatusL') }}
        </option>
        <option value="RESIDENCE_STATUS_F_PRELIMINARY_FOREIGNER">
          {{ $t('message.generic.residenceStatusF') }}
        </option>
        <option value="RESIDENCE_STATUS_N_SEEKING_ASYLUM">
          {{ $t('message.generic.residenceStatusN') }}
        </option>
        <option value="RESIDENCE_STATUS_S_PROTECTION_NEEDED">
          {{ $t('message.generic.residenceStatusS') }}
        </option>
        <option value="RESIDENCE_STATUS_NO_STATUS">
          {{ $t('message.generic.residenceStatusNoStatus') }}
        </option>
      </coozzy-form-select>
    </div>
    <div
      v-if="requiresResidenceStatus && (!residenceStatus || (residenceStatus !== 'UNDEFINED_RESIDENCE_STATUS' && residenceStatus !== 'RESIDENCE_STATUS_NO_STATUS'))"
      class="col-12 col-md-3">
      <coozzy-form-input
        :id="individualId"
        v-model="residenceStatusValidUntilDate"
        data-cy="application-form-individual-residence-status-valid-until-input"
        :filter="'formatDate'"
        :name="$t('message.generic.validUntil') + (sending ? '*' : '')"
        :state="$v.dates.residenceStatusValidUntil.$dirty && $v.dates.residenceStatusValidUntil.$error ? false : null"
        type="date"
        @blur="syncDateFieldsFromInputs" />
    </div>
    <div
      v-else-if="requiresResidenceStatus"
      class="col-12 col-md-3" />
    <!-- Contact info (phone, email) -->
    <div class="col-12 col-md-6">
      <coozzy-form-input
        :id="individualId"
        v-model="email"
        data-cy="application-form-individual-email-input"
        :name="$t('message.generic.email') + (isMainIndividualEmailRequired ? '*' : '')"
        :state="$v.email.$dirty && $v.email.$error ? false : null"
        type="text" />
    </div>
    <div class="col-12 col-md-6">
      <coozzy-form-input
        :id="individualId"
        v-model="phoneNumber"
        data-cy="application-form-individual-phone-home-input"
        :name="$tc('message.generic.phone.home1')"
        type="text" />
    </div>
    <div class="col-12 col-md-6">
      <coozzy-form-input
        :id="individualId"
        v-model="mobileNumber"
        data-cy="application-form-individual-mobile-input"
        :name="$t('message.generic.phone.mobile') + (sending ? '*' : '')"
        :state="$v.mobileNumber.$dirty && $v.mobileNumber.$error ? false : null"
        type="text" />
    </div>
    <!-- Civil status -->
    <div class="col-12 col-md-6">
      <label :for="`civilStatus${individualId}`">{{ $t('message.applicationForm.civilStatus.title') }}
      </label>
      <coozzy-form-select
        :id="`civilStatus${individualId}`"
        v-model="civilStatus"
        data-cy="application-form-individual-civil-status-select">
        <option value="UNDEFINED_CIVIL_STATUS">
          -
        </option>
        <option value="CIVIL_STATUS_SINGLE">
          {{ $t('message.applicationForm.civilStatus.single') }}
        </option>
        <option value="CIVIL_STATUS_MARRIED">
          {{ $t('message.applicationForm.civilStatus.married') }}
        </option>
        <option value="CIVIL_STATUS_DIVORCED">
          {{ $t('message.applicationForm.civilStatus.divorced') }}
        </option>
        <option value="CIVIL_STATUS_WIDOWED">
          {{ $t('message.applicationForm.civilStatus.widowed') }}
        </option>
        <option value="CIVIL_STATUS_SEPARATED">
          {{ $t('message.applicationForm.civilStatus.separated') }}
        </option>
        <option value="CIVIL_STATUS_REGISTERED_PARTNERSHIP">
          {{ $t('message.applicationForm.civilStatus.registeredPartnership') }}
        </option>
        <option value="CIVIL_STATUS_DISSOLVED_PARTNERSHIP">
          {{ $t('message.applicationForm.civilStatus.dissolvedPartnership') }}
        </option>
      </coozzy-form-select>
    </div>
    <!-- preferredCommunication -->
    <div
      v-if="!isGuarantor"
      class="col-12"
      :class="[requiresResidenceStatus ? 'col-md-6' : ' col-md-3']">
      <label :for="`preferredCommunication${individualId}`">{{ $t('message.contact.preferredCommunicationChannel') }}</label>
      <coozzy-form-select
        :id="`preferredCommunication${individualId}`"
        v-model="preferredCommunicationChannel"
        data-cy="application-form-individual-preferred-communication-select">
        <option value="PREFERRED_COMMUNICATION_CHANNEL_UNDEFINED">
          -
        </option>
        <option value="PREFERRED_COMMUNICATION_CHANNEL_EMAIL">
          {{ $t('message.contact.preferredCommunicationChannelType.email') }}
        </option>
        <option value="PREFERRED_COMMUNICATION_CHANNEL_LETTER">
          {{ $t('message.contact.preferredCommunicationChannelType.letter') }}
        </option>
      </coozzy-form-select>
    </div>
    <div
      v-if="isParkingSpace && !isGuarantor"
      class="col-12 col-md-6"
      :class="[requiresResidenceStatus ? '' : 'offset-md-3']">
      <coozzy-form-input
        :id="individualId + 'licensePlateNumber'"
        v-model="licensePlate"
        data-cy="application-form-individual-license-plate-input"
        :name="$tc('message.applicationForm.licensePlateNumber')"
        type="text" />
    </div>
    <div
      v-if="showHouseholdFields && !isGuarantor"
      class="col-12 col-md-6"
      :class="[requiresResidenceStatus ? '' : 'offset-md-3']">
      <div class="row">
        <div class="col-md-6">
          <label :for="`pets${individualId}`">{{ $t('message.applicationForm.pets') }}</label>
          <coozzy-form-select
            :id="`pets${individualId}`"
            v-model="pets"
            data-cy="application-form-individual-pets-select">
            <option value="UNDEFINED_VALUE">
              -
            </option>
            <option value="APPLIES">
              {{ $t('message.generic.selectField.yes') }}
            </option>
            <option value="DOES_NOT_APPLY">
              {{ $t('message.generic.selectField.no') }}
            </option>
          </coozzy-form-select>
        </div>
        <div class="col-md-6">
          <coozzy-form-input
            v-if="pets === 'APPLIES'"
            :id="individualId"
            v-model="petsDescription"
            data-cy="application-form-individual-pets-description-input"
            :name="$tc('message.applicationForm.petsDescription')"
            type="text" />
        </div>
      </div>
    </div>
    <div
      v-if="showHouseholdFields && !isGuarantor"
      class="col-12 col-md-3">
      <label :for="`instruments${individualId}`">{{ $t('message.applicationForm.instruments') }}</label>
      <coozzy-form-select
        :id="`instruments${individualId}`"
        v-model="instruments"
        data-cy="application-form-individual-instruments-select">
        <option value="UNDEFINED_VALUE">
          -
        </option>
        <option value="APPLIES">
          {{ $t('message.generic.selectField.yes') }}
        </option>
        <option value="DOES_NOT_APPLY">
          {{ $t('message.generic.selectField.no') }}
        </option>
      </coozzy-form-select>
    </div>
    <div
      v-if="showHouseholdFields && !isGuarantor"
      class="col-12 col-md-3">
      <coozzy-form-input
        v-if="instruments === 'APPLIES'"
        :id="individualId"
        v-model="instrumentsDescription"
        data-cy="application-form-individual-instruments-description-input"
        :name="String($t('message.applicationForm.instrumentsDescription'))"
        type="text" />
    </div>
    <div
      v-if="!isGuarantor"
      class="col-12 col-md-3">
      <label :for="`smokers${individualId}`">{{ $t('message.applicationForm.smokers') }}</label>
      <coozzy-form-select
        :id="`smokers${individualId}`"
        v-model="smokers"
        data-cy="application-form-individual-smokers-select">
        <option value="UNDEFINED_VALUE">
          -
        </option>
        <option value="APPLIES">
          {{ $t('message.generic.selectField.yes') }}
        </option>
        <option value="DOES_NOT_APPLY">
          {{ $t('message.generic.selectField.no') }}
        </option>
      </coozzy-form-select>
    </div>
    <div
      v-if="showHouseholdFields && !isGuarantor"
      class="col-12 col-md-6">
      <label :for="`tvProvider-${individualId}`">
        {{ $t('message.applicationForm.tvProvider.title') }}
        <coozzy-info-circle-icon
          v-b-tooltip.hover.html="
            $t('message.applicationForm.tvProvider.tooltip')
          "
          class="ml-1" />
      </label>
      <coozzy-form-select
        :id="`tvProvider-${individualId}`"
        v-model="tvProvider"
        data-cy="application-form-individual-tv-provider-select">
        <option value="TV_PROVIDER_UNDEFINED">
          -
        </option>
        <option value="TV_PROVIDER_UPC">
          {{ $t('message.applicationForm.tvProvider.types.TV_PROVIDER_UPC') }}
        </option>
        <option value="TV_PROVIDER_THIRD_PARTY">
          {{ $t('message.applicationForm.tvProvider.types.TV_PROVIDER_THIRD_PARTY') }}
        </option>
      </coozzy-form-select>
    </div>
  </div>
</template>

<script lang="ts">
import CoozzyFormRadio from '@/framework/components/form/radio/CoozzyFormRadio.vue'
import CoozzyFormInput from '@/framework/components/form/input/CoozzyFormInput.vue'
import { mapGetters, mapMutations } from 'vuex'
import CoozzyFormSelect from '@/framework/components/form/select/CoozzyFormSelect.vue'
import {
  noFutureDateObject,
  validation,
  isZipValid,
  validBirth,
  isEmailValid
} from '@/mixins/validation'
import { required } from 'vuelidate/lib/validators'
import { user } from '@/mixins/user'
import * as Sentry from '@sentry/vue'
import { address } from '@/mixins/address'
import CoozzyFormCountrySelect from '@/framework/components/form/select/CoozzyFormCountrySelect.vue'
import CoozzyFormCheckbox from '@/framework/components/form/checkbox/CoozzyFormCheckbox.vue'
import CoozzyFormRegionSelect from '@/framework/components/form/select/CoozzyFormRegionSelect.vue'
import CoozzyFormCountryCitizenshipSelect from '@/framework/components/form/select/CoozzyFormCountryCitizenshipSelect.vue'
import CoozzyInfoCircleIcon from '@/framework/components/icons/CoozzyInfoCircleIcon.vue'
import Vue from 'vue'
import {
  ApplicationFormIndividualBasicCityOption,
  ApplicationFormIndividualBasicDataState
} from '@/types/models'

function notUndefined(value) {
  return value !== 'UNDEFINED_RESIDENCE_STATUS'
}
function notEqualTo(otherEmail) {
  return function (value) {
    return !value || value !== otherEmail
  }
}
export default Vue.extend({
  name: 'ApplicationFormIndividualBasic',
  components: {
    CoozzyInfoCircleIcon,
    CoozzyFormRegionSelect,
    CoozzyFormCheckbox,
    CoozzyFormCountrySelect,
    CoozzyFormCountryCitizenshipSelect,
    CoozzyFormSelect,
    CoozzyFormInput,
    CoozzyFormRadio
  },
  mixins: [user, validation, address],
  props: {
    individualId: {
      type: String,
      required: true
    },
    sending: {
      type: Boolean,
      default: false
    },
    changedAddress: {
      type: String,
      default: ''
    },
    object: {
      type: Object,
      default: null
    },
    emailMainTenant: {
      type: String,
      default: ''
    },
    isGuarantor: {
      type: Boolean,
      default: false
    }
  },
  data(): ApplicationFormIndividualBasicDataState {
    return {
      isCitiesSuggested: false,
      citiesValue: [],
      dates: {
        dob: null,
        residenceStatusValidUntil: null,
        desiredStartOfContract: null
      },
      sameAddressAsMainTenant: false,
      citySelect: null
    }
  },
  computed: {
    ...mapGetters('applicationForm', [
      'getIndividualByInternalId',
      'getMainIndividual',
      'getViewingDone',
      'getDesiredStartOfContract'
    ]),
    isParkingSpace() {
      return this.object && this.object.category === 'PARKING_SPACE'
    },
    showHouseholdFields() {
      return (
        this.object &&
        this.object.category !== 'PARKING_SPACE' &&
        this.object.category !== 'SECONDARY_ROOM'
      )
    },
    individual() {
      return this.getIndividualByInternalId(this.individualId)
    },
    mainIndividual() {
      return this.getMainIndividual()
    },
    mainIndividualId(): string {
      return (
        (this.mainIndividual as { internalId?: string } | null)?.internalId ||
        ''
      )
    },
    isMainIndividual() {
      return this.individual.mainIndividual
    },
    isMainIndividualEmailRequired(): boolean {
      return (
        this.sending &&
        Boolean(
          (this.individual as { mainIndividual?: boolean } | null)
            ?.mainIndividual
        )
      )
    },
    requiresResidenceStatus() {
      return this.nationality && this.nationality !== 'CH'
    },
    zipLength() {
      if (this.individual) {
        const country = this.individual.address.country
        if (country === 'CH') {
          return 4
        } else if (country === 'DE') {
          return 5
        } else {
          return 15
        }
      }
      return -1
    },
    cityLabels(): string[] {
      return this.citiesValue.map(
        (cityOption: ApplicationFormIndividualBasicCityOption) =>
          cityOption.city
      )
    },
    relationToMainIndividual: {
      get() {
        return this.individual.tenancy.relationToMainIndividual
      },
      set(value) {
        const ind = this.individual
        ind.tenancy.relationToMainIndividual = value
        this.updateIndividual(ind)
      }
    },
    gender: {
      get() {
        return this.individual.gender
      },
      set(value) {
        if (this.individual.gender === value) {
          return
        }
        const ind = this.individual
        ind.gender = value
        this.updateIndividual(ind)
      }
    },
    firstName: {
      get() {
        return this.individual.firstName
      },
      set(value) {
        const ind = this.individual
        ind.firstName = value
        this.updateIndividual(ind)
      }
    },
    lastName: {
      get() {
        return this.individual.lastName
      },
      set(value) {
        const ind = this.individual
        ind.lastName = value
        this.updateIndividual(ind)
      }
    },
    street: {
      get() {
        return this.individual.address.street
      },
      set(value) {
        const ind = this.individual
        ind.address.street = value
        this.updateIndividual(ind)
      }
    },
    streetNumber: {
      get() {
        return this.individual.address.streetNumber
      },
      set(value) {
        const ind = this.individual
        ind.address.streetNumber = value
        this.updateIndividual(ind)
      }
    },
    zip: {
      get() {
        return this.individual.address.zip
      },
      set(value) {
        const ind = this.individual
        ind.address.zip = value
        this.updateIndividual(ind)
      }
    },
    city: {
      get() {
        return this.individual.address.city
      },
      set(value) {
        const ind = this.individual
        ind.address.city = value
        this.updateIndividual(ind)
      }
    },
    country: {
      get() {
        return this.individual.address.country
      },
      set(value) {
        const ind = this.individual
        ind.address.country = value
        this.updateIndividual(ind)
      }
    },
    state: {
      get() {
        return this.individual.address.state
      },
      set(value) {
        const ind = this.individual
        ind.address.state = value
        this.updateIndividual(ind)
      }
    },
    dob: {
      get() {
        return this.individual.dob
      },
      set(value) {
        const ind = this.individual
        ind.dob = value
        this.updateIndividual(ind)
      }
    },
    nationality: {
      get() {
        return this.individual.nationality
      },
      set(value) {
        const ind = this.individual
        ind.nationality = value
        this.updateIndividual(ind)
      }
    },
    residenceStatus: {
      get() {
        return this.individual.residenceStatus
          ? this.individual.residenceStatus
          : 'UNDEFINED_RESIDENCE_STATUS'
      },
      set(value) {
        const ind = this.individual
        ind.residenceStatus = value
        this.updateIndividual(ind)
      }
    },
    residenceStatusValidUntil: {
      get() {
        return this.individual.residenceStatusValidUntil
      },
      set(value) {
        const ind = this.individual
        ind.residenceStatusValidUntil = value
        this.updateIndividual(ind)
      }
    },
    email: {
      get() {
        return this.individual.email
      },
      set(value) {
        const ind = this.individual
        ind.email = value
        this.updateIndividual(ind)
      }
    },
    phoneNumber: {
      get() {
        return this.individual.phoneNumber
      },
      set(value) {
        const ind = this.individual
        ind.phoneNumber = value
        this.updateIndividual(ind)
      }
    },
    mobileNumber: {
      get() {
        return this.individual.mobileNumber
      },
      set(value) {
        const ind = this.individual
        ind.mobileNumber = value
        this.updateIndividual(ind)
      }
    },
    civilStatus: {
      get() {
        return this.individual.civilStatus
      },
      set(value) {
        const ind = this.individual
        ind.civilStatus = value
        this.updateIndividual(ind)
      }
    },
    licensePlate: {
      get() {
        return this.individual.household.licensePlate
      },
      set(value) {
        const ind = this.individual
        ind.household.licensePlate = value
        this.updateIndividual(ind)
      }
    },
    pets: {
      get() {
        return this.individual.household.pets
      },
      set(value) {
        const ind = this.individual
        ind.household.pets = value
        this.updateIndividual(ind)
      }
    },
    petsDescription: {
      get() {
        return this.individual.household.petsDescription
      },
      set(value) {
        const ind = this.individual
        ind.household.petsDescription = value
        this.updateIndividual(ind)
      }
    },
    instruments: {
      get() {
        return this.individual.household.instruments
      },
      set(value) {
        const ind = this.individual
        ind.household.instruments = value
        this.updateIndividual(ind)
      }
    },
    instrumentsDescription: {
      get() {
        return this.individual.household.instrumentsDescription
      },
      set(value) {
        const ind = this.individual
        ind.household.instrumentsDescription = value
        this.updateIndividual(ind)
      }
    },
    smokers: {
      get() {
        return this.individual.household.smokers
      },
      set(value) {
        const ind = this.individual
        ind.household.smokers = value
        this.updateIndividual(ind)
      }
    },
    tvProvider: {
      get() {
        return this.individual.household.tvProvider
      },
      set(value) {
        const ind = this.individual
        ind.household.tvProvider = value
        this.updateIndividual(ind)
      }
    },
    preferredCommunicationChannel: {
      get() {
        return this.individual.preferredCommunicationChannel
      },
      set(value) {
        const ind = this.individual
        ind.preferredCommunicationChannel = value
        this.updateIndividual(ind)
      }
    },
    viewingDone: {
      get() {
        return this.getViewingDone() === true ? 'yes' : 'no'
      },
      set(value) {
        value === 'yes' ? this.setViewingDone(true) : this.setViewingDone(false)
      }
    },
    preferredLanguage: {
      get() {
        return this.individual.preferredLanguage
      },
      set(value) {
        const ind = this.individual
        ind.preferredLanguage = value
        if (
          ind.preferredLanguage === 'PREFERRED_LANGUAGE_UNDEFINED' ||
          ind.preferredLanguage === 'PREFERRED_LANGUAGE_GERMAN'
        ) {
          this.$store
            .dispatch('internationalization/changeCurrentLanguage', 'de')
            .then()
        } else if (ind.preferredLanguage === 'PREFERRED_LANGUAGE_ENGLISH') {
          this.$store
            .dispatch('internationalization/changeCurrentLanguage', 'en')
            .then()
        } else if (ind.preferredLanguage === 'PREFERRED_LANGUAGE_FRENCH') {
          this.$store
            .dispatch('internationalization/changeCurrentLanguage', 'fr')
            .then()
        } else {
          this.$store
            .dispatch('internationalization/changeCurrentLanguage', 'it')
            .then()
        }
        this.updateIndividual(ind)
      }
    },
    desiredStartOfContract: {
      get() {
        return this.getDesiredStartOfContract()
      },
      set(value) {
        this.setDesiredStartOfContract(value)
      }
    },
    desiredStartOfContractDate: {
      get(): string | null {
        return this.dates.desiredStartOfContract
      },
      set(value: string | null) {
        this.dates.desiredStartOfContract = value
      }
    },
    dobDate: {
      get(): string | null {
        return this.dates.dob
      },
      set(value: string | null) {
        this.dates.dob = value
      }
    },
    residenceStatusValidUntilDate: {
      get(): string | null {
        return this.dates.residenceStatusValidUntil
      },
      set(value: string | null) {
        this.dates.residenceStatusValidUntil = value
      }
    }
  },
  watch: {
    changedAddress: {
      immediate: true,
      deep: true,
      handler() {
        if (this.sameAddressAsMainTenant) {
          this.copyAddress()
        }
      }
    },
    sameAddressAsMainTenant: function (newVal) {
      if (newVal === true) {
        this.copyAddress()
        const ind = this.individual
        ind.tenancy.sameAsMainIndividual = newVal
        this.updateIndividual(ind)
      } else {
        const ind = this.individual
        ind.tenancy.sameAsMainIndividual = newVal
        this.updateIndividual(ind)
      }
    },
    street: function (newVal) {
      if (this.isMainIndividual) {
        this.$emit('address-changed', newVal)
      }
    },
    streetNumber: function (newVal) {
      if (this.isMainIndividual) {
        this.$emit('address-changed', newVal)
      }
    },
    zip: function (newVal) {
      if (this.isMainIndividual) {
        this.$emit('address-changed', newVal)
      }
    },
    city: function (newVal) {
      if (this.isMainIndividual) {
        this.$emit('address-changed', newVal)
      }
    },
    country: function (newVal) {
      if (this.isMainIndividual) {
        this.$emit('address-changed', newVal)
      }
    },
    state: function (newVal) {
      if (this.isMainIndividual) {
        this.$emit('address-changed', newVal)
      }
    },
    'individual.nationality': function () {
      if (!this.requiresResidenceStatus) {
        this.residenceStatus = null
        this.residenceStatusValidUntil = null
        this.dates.residenceStatusValidUntil = null
      }
    },
    residenceStatus: function (value) {
      if (value === 'UNDEFINED_RESIDENCE_STATUS' || value === 'RESIDENCE_STATUS_NO_STATUS') {
        this.residenceStatusValidUntil = null
        this.dates.residenceStatusValidUntil = null
      }
    }
  },
  mounted() {
    this.setUserpreferredLanguage()
    this.dates.dob = this.dob?.year
      ? `${this.dob.year}-${this.dob.month
          .toString()
          .padStart(2, '0')}-${this.dob.day.toString().padStart(2, '0')}`
      : null
    this.dates.residenceStatusValidUntil = this.residenceStatusValidUntil?.year
      ? `${
          this.residenceStatusValidUntil.year
        }-${this.residenceStatusValidUntil.month
          .toString()
          .padStart(2, '0')}-${this.residenceStatusValidUntil.day
          .toString()
          .padStart(2, '0')}`
      : null
    this.dates.desiredStartOfContract =
      this.desiredStartOfContract?.year &&
      this.desiredStartOfContract.month?.toString() &&
      this.desiredStartOfContract.day?.toString()
        ? `${
            this.desiredStartOfContract.year
          }-${this.desiredStartOfContract.month
            .toString()
            .padStart(2, '0')}-${this.desiredStartOfContract.day
            .toString()
            .padStart(2, '0')}`
        : null
  },
  methods: {
    ...mapMutations('applicationForm', [
      'updateIndividual',
      'setViewingDone',
      'setDesiredStartOfContract'
    ]),
    setUserpreferredLanguage() {
      const userPreferredLanguage = this.currentLanguage
      if (userPreferredLanguage === 'en') {
        this.preferredLanguage = 'PREFERRED_LANGUAGE_ENGLISH'
      } else if (userPreferredLanguage === 'it') {
        this.preferredLanguage = 'PREFERRED_LANGUAGE_ITALIAN'
      } else if (userPreferredLanguage === 'fr') {
        this.preferredLanguage = 'PREFERRED_LANGUAGE_FRENCH'
      } else if (userPreferredLanguage === 'de') {
        this.preferredLanguage = 'PREFERRED_LANGUAGE_GERMAN'
      }
    },
    tabClicked() {
      if (this.citiesValue.length > 0) {
        this.citySelect = this.citiesValue[0].city
        this.cityChanged(this.citiesValue[0])
      }
    },
    copyAddress() {
      // this.$nextTick(() => {
      this.street = this.mainIndividual.address.street
      this.streetNumber = this.mainIndividual.address.streetNumber
      this.zip = this.mainIndividual.address.zip
      this.city = this.mainIndividual.address.city
      this.country = this.mainIndividual.address.country
      this.$nextTick(() => {
        this.state = this.mainIndividual.address.state
      })
    },
    syncDateFieldsFromInputs() {
      if (this.dates.dob) {
        const date = new Date(this.dates.dob)
        this.dob = {
          year: date.getFullYear(),
          month: date.getMonth() + 1,
          day: date.getDate()
        }
      }
      if (this.dates.residenceStatusValidUntil !== null) {
        const dateResidenceStatusValidUntil = new Date(
          this.dates.residenceStatusValidUntil
        )
        this.residenceStatusValidUntil = {
          year: dateResidenceStatusValidUntil.getFullYear(),
          month: dateResidenceStatusValidUntil.getMonth() + 1,
          day: dateResidenceStatusValidUntil.getDate()
        }
      }
      if (this.dates.desiredStartOfContract) {
        const date = new Date(this.dates.desiredStartOfContract)
        this.desiredStartOfContract = {
          year: date.getFullYear(),
          month: date.getMonth() + 1,
          day: date.getDate()
        }
      }
    },
    cityChanged(event: ApplicationFormIndividualBasicCityOption) {
      this.city = event.city
      this.state = event.state
    },
    getCityLabel(
      item: ApplicationFormIndividualBasicCityOption | number
    ): string {
      return typeof item === 'number' ? '' : item.city
    },
    async getCityFromZip() {
      const zip = String(this.zip ?? '')
      if (zip.length > 3 && this.country === 'CH') {
        const zipData = await this.getZipInfo(zip)
        if (zipData) {
          this.state = zipData.state
          if (zipData.cities.length > 1) {
            this.isCitiesSuggested = true
            this.citiesValue = zipData.cities
          } else {
            this.isCitiesSuggested = false
            this.$nextTick(() => {
              this.city = zipData.cities[0].city
            })
          }
        }
      }
    },
    syncResidenceValidUntilFromInput() {
      const date = new Date(this.dates.residenceStatusValidUntil)
      this.residenceStatusValidUntil = {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate()
      }
    },
    isInvalid() {
      this.$v.$touch()
      if (this.$v.$invalid) {
        Sentry.setExtra(
          'individualBasicValidation-' + this.individualId,
          this.$v
        )
        this.scrollToError()
        this.$nextTick(() => {
          if (
            this.$v.dates.dob.$invalid &&
            this.$v.dates.dob.$error &&
            !this.$v.dates.dob.validBirth
          ) {
            Vue.toasted.show(this.$t('message.savingErrors.birthDate'), {
              type: 'error'
            })
          }
        })
        return true
      }
      return false
    }
  },
  validations() {
    const isResidenceStatusValidUntilRequired = (value: string) => {
      if (this.residenceStatus === 'UNDEFINED_RESIDENCE_STATUS' || this.residenceStatus === 'RESIDENCE_STATUS_NO_STATUS') {
        return true
      }
      return !!value
    }

    const datesValidation = {
      ...(this.isMainIndividual
        ? {
            desiredStartOfContract: { required },
            dob: { required, noFutureDateObject, validBirth }
          }
        : {
            dob: { required, noFutureDateObject, validBirth }
          }),
      ...(this.requiresResidenceStatus
        ? {
            residenceStatusValidUntil: {
              required: isResidenceStatusValidUntilRequired
            }
          }
        : {})
    }

    const emailValidation = this.isMainIndividual
      ? {
          isEmailValid,
          required
        }
      : {
          isEmailValid,
          notEqualTo: notEqualTo(this.emailMainTenant)
        }

    return {
      gender: { required },
      firstName: { required },
      lastName: { required },
      street: { required },
      streetNumber: { required },
      zip: {
        required,
        isZipValid: isZipValid(this.individual.address)
      },
      city: { required },
      country: { required },
      state: { required },
      nationality: { required },
      mobileNumber: { required },
      ...(this.isMainIndividual ? {} : { relationToMainIndividual: { required } }),
      ...(this.requiresResidenceStatus
        ? {
            residenceStatus: {
              required,
              notUndefined
            }
          }
        : {}),
      dates: datesValidation,
      email: emailValidation
    }
  }
})
</script>

<style lang="scss" scoped>
.custom-radio {
  display: inline-block;
}
</style>
